import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Container,
  Card,
  CardContent,
  Avatar,
  IconButton,
  AppBar,
  Toolbar,
  Button,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
  Switch,
  FormControlLabel,
  Divider,
  InputAdornment,
} from '@mui/material';
import {
  Coffee,
  QrCode2,
  Image as ImageIcon,
  TrendingUp,
  Logout,
  Edit,
  LocationOn,
  CalendarToday,
  Favorite,
  Visibility,
  VisibilityOff,
  Lock,
} from '@mui/icons-material';
import { useCafeOwnerAuth } from '../contexts/CafeOwnerAuthContext';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import axios from 'axios';
import PhotoManager from '../components/PhotoManager';
import GiftCardVerification from '../components/GiftCardVerification';

// Available features with icons (maksimum 8 seçilebilir)
const CAFE_FEATURES = [
  { key: 'wifi', label: 'Ücretsiz WiFi', icon: '📶' },
  { key: 'priz', label: 'Bol Priz', icon: '🔌' },
  { key: 'kartlaOdeme', label: 'Kartla Ödeme', icon: '💳' },
  { key: 'terrace', label: 'Teras', icon: '☀️' },
  { key: 'parking', label: 'Otopark', icon: '🅿️' },
  { key: 'petFriendly', label: 'Evcil Hayvan Dostu', icon: '🐾' },
  { key: 'quietArea', label: 'Sessiz Çalışma Alanı', icon: '🤫' },
  { key: 'outdoorSeating', label: 'Açık Hava Oturma', icon: '🌳' },
  { key: 'airConditioning', label: 'Klima', icon: '❄️' },
  { key: 'meetingRoom', label: 'Toplantı Odası', icon: '👥' },
  { key: 'fastWifi', label: 'Hızlı WiFi', icon: '⚡' },
  { key: 'breakfast', label: 'Kahvaltı Servisi', icon: '☕' },
  { key: 'longTables', label: 'Uzun Masa', icon: '📏' },
  { key: 'chargingStation', label: 'Şarj İstasyonu', icon: '🔋' },
  { key: 'liveMusic', label: 'Canlı Müzik', icon: '🎵' },
];

const MAX_FEATURES = 8;

interface Statistics {
  cafe: {
    id: string;
    name: string;
    address: string;
  };
  qrScans: {
    total: number;
    last7Days: number;
    last30Days: number;
    byDate: Record<string, number>;
  };
  posts: {
    total: number;
    last7Days: number;
    last30Days: number;
    byDate: Record<string, number>;
    uniqueUsers: number;
  };
  favorites: {
    total: number;
  };
  recentActivity: {
    recentScans: Array<{ id: string; scannedAt: Date }>;
    recentPosts: Array<{
      id: string;
      userId: string;
      userName: string;
      imageUrls: string[];
      caption: string;
      createdAt: Date;
      likeCount: number;
    }>;
  };
}

const CafeOwnerDashboardPage: React.FC = () => {
  const navigate = useNavigate();
  const { cafeOwner, logout, isAuthenticated } = useCafeOwnerAuth();

  const [statistics, setStatistics] = useState<Statistics | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Edit dialog states
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editName, setEditName] = useState('');
  const [editAddress, setEditAddress] = useState('');
  const [editPhoto, setEditPhoto] = useState('');
  const [editPhotoURLs, setEditPhotoURLs] = useState<string[]>([]);
  const [editAnonymousMatching, setEditAnonymousMatching] = useState(true);
  const [editFeatures, setEditFeatures] = useState<string[]>([]);
  const [updating, setUpdating] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' });

  // Password change dialog states
  const [passwordDialogOpen, setPasswordDialogOpen] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [changingPassword, setChangingPassword] = useState(false);
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const fetchStatistics = React.useCallback(async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('cafeOwnerToken');

      const response = await axios.get('https://api.sobcof.com/api/cafe-owner/statistics', {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.data.success) {
        setStatistics(response.data.data);
        setEditName(response.data.data.cafe.name);
        setEditAddress(response.data.data.cafe.address);

        // Get current cafe photo and settings
        const cafeResponse = await axios.get(`https://api.sobcof.com/api/partner-cafes`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });

        // Find our cafe
        const ourCafe = cafeResponse.data.data?.find((c: any) => c.id === cafeOwner?.cafeId);
        if (ourCafe) {
          if (ourCafe.photoURLs && ourCafe.photoURLs.length > 0) {
            setEditPhoto(ourCafe.photoURLs[0]);
            setEditPhotoURLs(ourCafe.photoURLs);
          } else {
            setEditPhotoURLs([]);
          }
          setEditAnonymousMatching(ourCafe.allowsAnonymousMatching !== false);
          setEditFeatures(ourCafe.features || []);
        }
      }
    } catch (error) {
      console.error('Error fetching statistics:', error);
      setError('İstatistikler yüklenirken hata oluştu');
    } finally {
      setLoading(false);
    }
  }, [cafeOwner?.cafeId]);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/cafe-partnership/login');
      return;
    }
    fetchStatistics();
  }, [isAuthenticated, navigate, fetchStatistics]);

  const handleLogout = () => {
    logout();
    navigate('/cafe-partnership/login');
  };

  const handleOpenEditDialog = () => {
    setEditDialogOpen(true);
  };

  const handleUpdateCafe = async () => {
    try {
      setUpdating(true);
      const token = localStorage.getItem('cafeOwnerToken');

      // Use first photo from photoURLs array as main photo
      const mainPhotoURL = editPhotoURLs.length > 0 ? editPhotoURLs[0] : '';

      await axios.put('https://api.sobcof.com/api/cafe-owner/update-cafe', {
        name: editName,
        address: editAddress,
        photoURL: mainPhotoURL,
        allowsAnonymousMatching: editAnonymousMatching,
        features: editFeatures
      }, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      setSnackbar({
        open: true,
        message: 'Kafe bilgileri başarıyla güncellendi',
        severity: 'success'
      });

      setEditDialogOpen(false);
      fetchStatistics();
    } catch (error: any) {
      setSnackbar({
        open: true,
        message: error.response?.data?.message || 'Güncelleme sırasında hata oluştu',
        severity: 'error'
      });
    } finally {
      setUpdating(false);
    }
  };

  const handleChangePassword = async () => {
    try {
      // Validation
      if (!currentPassword || !newPassword || !confirmPassword) {
        setSnackbar({
          open: true,
          message: 'Lütfen tüm alanları doldurun',
          severity: 'error'
        });
        return;
      }

      if (newPassword.length < 6) {
        setSnackbar({
          open: true,
          message: 'Yeni şifre en az 6 karakter olmalıdır',
          severity: 'error'
        });
        return;
      }

      if (newPassword !== confirmPassword) {
        setSnackbar({
          open: true,
          message: 'Yeni şifreler eşleşmiyor',
          severity: 'error'
        });
        return;
      }

      setChangingPassword(true);
      const token = localStorage.getItem('cafeOwnerToken');

      await axios.post('https://api.sobcof.com/api/cafe-owner/change-password', {
        currentPassword,
        newPassword
      }, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      setSnackbar({
        open: true,
        message: 'Şifreniz başarıyla değiştirildi',
        severity: 'success'
      });

      // Reset form and close dialog
      setPasswordDialogOpen(false);
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');

    } catch (error: any) {
      setSnackbar({
        open: true,
        message: error.response?.data?.message || 'Şifre değiştirilirken hata oluştu',
        severity: 'error'
      });
    } finally {
      setChangingPassword(false);
    }
  };

  // Prepare chart data
  const getChartData = () => {
    if (!statistics) return [];

    const qrByDate = statistics.qrScans.byDate;
    const postsByDate = statistics.posts.byDate;

    const allDates = new Set([...Object.keys(qrByDate), ...Object.keys(postsByDate)]);

    return Array.from(allDates)
      .sort()
      .slice(-30) // Last 30 days
      .map(date => ({
        date: new Date(date).toLocaleDateString('tr-TR', { month: 'short', day: 'numeric' }),
        'QR Tarama': qrByDate[date] || 0,
        'Paylaşımlar': postsByDate[date] || 0
      }));
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !statistics) {
    return (
      <Container sx={{ mt: 4 }}>
        <Alert severity="error">{error || 'Veri yüklenemedi'}</Alert>
      </Container>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#f5f5f5' }}>
      {/* App Bar */}
      <AppBar position="static" sx={{ background: 'linear-gradient(135deg, #8B4513 0%, #D2691E 100%)' }}>
        <Toolbar sx={{ flexWrap: 'wrap', gap: 1 }}>
          <Typography
            variant="h6"
            sx={{
              flexGrow: 1,
              fontWeight: 700,
              color: 'white',
              fontSize: { xs: '1rem', sm: '1.3rem' }
            }}
          >
            SobCof Partner Paneli
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            <Button
              color="inherit"
              startIcon={<Lock />}
              onClick={() => setPasswordDialogOpen(true)}
              sx={{
                fontWeight: 600,
                fontSize: { xs: '0.75rem', sm: '0.875rem' },
                px: { xs: 1, sm: 2 },
                '&:hover': {
                  backgroundColor: 'rgba(255,255,255,0.1)'
                }
              }}
            >
              <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>Şifre Değiştir</Box>
              <Box component="span" sx={{ display: { xs: 'inline', sm: 'none' } }}>Şifre</Box>
            </Button>
            <Button
              color="inherit"
              startIcon={<Edit />}
              onClick={handleOpenEditDialog}
              sx={{
                fontWeight: 600,
                fontSize: { xs: '0.75rem', sm: '0.875rem' },
                px: { xs: 1, sm: 2 },
                '&:hover': {
                  backgroundColor: 'rgba(255,255,255,0.1)'
                }
              }}
            >
              <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>Kafe Bilgileri</Box>
              <Box component="span" sx={{ display: { xs: 'inline', sm: 'none' } }}>Düzenle</Box>
            </Button>
            <IconButton color="inherit" onClick={handleLogout} title="Çıkış Yap" size="small">
              <Logout />
            </IconButton>
          </Box>
        </Toolbar>
      </AppBar>

      <Container maxWidth="xl" sx={{ mt: { xs: 2, sm: 4 }, mb: { xs: 2, sm: 4 }, px: { xs: 2, sm: 3 } }}>
        {/* Cafe Info Card */}
        <Card sx={{ mb: 3, borderRadius: 3, boxShadow: 4 }}>
          <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1.5, sm: 2 }, flexDirection: { xs: 'column', sm: 'row' }, textAlign: { xs: 'center', sm: 'left' } }}>
              {editPhoto ? (
                <Avatar
                  src={editPhoto}
                  sx={{
                    width: { xs: 80, sm: 100 },
                    height: { xs: 80, sm: 100 },
                    border: '4px solid #8B4513',
                    boxShadow: 3
                  }}
                />
              ) : (
                <Avatar sx={{ width: { xs: 80, sm: 100 }, height: { xs: 80, sm: 100 }, bgcolor: 'primary.main', boxShadow: 3 }}>
                  <Coffee sx={{ fontSize: { xs: 40, sm: 50 } }} />
                </Avatar>
              )}
              <Box sx={{ flexGrow: 1 }}>
                <Typography variant="h4" sx={{ fontWeight: 800, mb: 1, color: '#8B4513', fontSize: { xs: '1.5rem', sm: '2rem' } }}>
                  {statistics.cafe.name}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'text.secondary', justifyContent: { xs: 'center', sm: 'flex-start' } }}>
                  <LocationOn fontSize="small" />
                  <Typography variant="body1" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>{statistics.cafe.address}</Typography>
                </Box>
              </Box>
            </Box>
          </CardContent>
        </Card>

        {/* Statistics Cards */}
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' }, gap: 3, mb: 4 }}>
            <Card sx={{ borderRadius: 3, boxShadow: 3 }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      Toplam QR Tarama
                    </Typography>
                    <Typography variant="h3" sx={{ fontWeight: 700, color: 'primary.main' }}>
                      {statistics.qrScans.total}
                    </Typography>
                  </Box>
                  <Avatar sx={{ bgcolor: 'primary.light', width: 56, height: 56 }}>
                    <QrCode2 sx={{ fontSize: 32 }} />
                  </Avatar>
                </Box>
              </CardContent>
            </Card>

            <Card sx={{ borderRadius: 3, boxShadow: 3 }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      Toplam Paylaşım
                    </Typography>
                    <Typography variant="h3" sx={{ fontWeight: 700, color: 'success.main' }}>
                      {statistics.posts.total}
                    </Typography>
                  </Box>
                  <Avatar sx={{ bgcolor: 'success.light', width: 56, height: 56 }}>
                    <ImageIcon sx={{ fontSize: 32 }} />
                  </Avatar>
                </Box>
              </CardContent>
            </Card>

            <Card sx={{ borderRadius: 3, boxShadow: 3 }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      Favorileyen
                    </Typography>
                    <Typography variant="h3" sx={{ fontWeight: 700, color: 'error.main' }}>
                      {statistics.favorites.total}
                    </Typography>
                  </Box>
                  <Avatar sx={{ bgcolor: 'error.light', width: 56, height: 56 }}>
                    <Favorite sx={{ fontSize: 32 }} />
                  </Avatar>
                </Box>
              </CardContent>
            </Card>

            <Card sx={{ borderRadius: 3, boxShadow: 3 }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      Son 7 Gün QR
                    </Typography>
                    <Typography variant="h3" sx={{ fontWeight: 700, color: 'warning.main' }}>
                      {statistics.qrScans.last7Days}
                    </Typography>
                  </Box>
                  <Avatar sx={{ bgcolor: 'warning.light', width: 56, height: 56 }}>
                    <TrendingUp sx={{ fontSize: 32 }} />
                  </Avatar>
                </Box>
              </CardContent>
            </Card>
        </Box>

        {/* Gift Card Verification */}
        <Card sx={{ mb: 4, borderRadius: 3, boxShadow: 4 }}>
          <CardContent>
            <GiftCardVerification
              cafeId={cafeOwner?.cafeId || ''}
              cafeOwnerId={cafeOwner?.id || ''}
            />
          </CardContent>
        </Card>

        {/* Chart */}
        <Card sx={{ mb: 4, borderRadius: 3 }}>
          <CardContent>
            <Typography variant="h6" sx={{ mb: 3, fontWeight: 600 }}>
              Son 30 Gün Aktivite Grafiği
            </Typography>
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={getChartData()}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Line type="monotone" dataKey="QR Tarama" stroke="#8B4513" strokeWidth={2} />
                <Line type="monotone" dataKey="Paylaşımlar" stroke="#2e7d32" strokeWidth={2} />
              </LineChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        {/* Recent Activities */}
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' }, gap: 3 }}>
          {/* Recent QR Scans */}
            <Card sx={{ borderRadius: 3, height: '100%' }}>
              <CardContent>
                <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                  Son QR Taramalar
                </Typography>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Tarih</TableCell>
                        <TableCell>Saat</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {statistics.recentActivity.recentScans.map((scan, index) => (
                        <TableRow key={scan.id || index}>
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <CalendarToday fontSize="small" color="primary" />
                              {new Date(scan.scannedAt).toLocaleDateString('tr-TR')}
                            </Box>
                          </TableCell>
                          <TableCell>
                            {new Date(scan.scannedAt).toLocaleTimeString('tr-TR', {
                              hour: '2-digit',
                              minute: '2-digit'
                            })}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
                {statistics.recentActivity.recentScans.length === 0 && (
                  <Alert severity="info" sx={{ mt: 2 }}>Henüz QR tarama yok</Alert>
                )}
              </CardContent>
            </Card>

          {/* Recent Posts */}
            <Card sx={{ borderRadius: 3, height: '100%' }}>
              <CardContent>
                <Typography variant="h6" sx={{ mb: 3, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <ImageIcon color="primary" />
                  Son Paylaşımlar
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                  {statistics.recentActivity.recentPosts.map((post) => (
                    <Card key={post.id} variant="outlined" sx={{ borderRadius: 3, overflow: 'hidden' }}>
                      {/* Post Image */}
                      {post.imageUrls && post.imageUrls.length > 0 && (
                        <Box
                          component="img"
                          src={post.imageUrls[0]}
                          alt="Post"
                          sx={{
                            width: '100%',
                            height: 200,
                            objectFit: 'cover'
                          }}
                        />
                      )}

                      {/* Post Content */}
                      <Box sx={{ p: 2 }}>
                        {/* User Info */}
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                          <Avatar
                            sx={{
                              width: 40,
                              height: 40,
                              bgcolor: 'primary.main',
                              fontWeight: 600
                            }}
                          >
                            {post.userName ? post.userName[0].toUpperCase() : '?'}
                          </Avatar>
                          <Box>
                            <Typography variant="subtitle2" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
                              @{post.userName}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {new Date(post.createdAt).toLocaleDateString('tr-TR', {
                                day: 'numeric',
                                month: 'short',
                                year: 'numeric',
                                hour: '2-digit',
                                minute: '2-digit'
                              })}
                            </Typography>
                          </Box>
                        </Box>

                        {/* Post Caption/Description */}
                        {post.caption && (
                          <Typography
                            variant="body2"
                            sx={{
                              mb: 2,
                              color: 'text.primary',
                              lineHeight: 1.5
                            }}
                          >
                            {post.caption}
                          </Typography>
                        )}

                        {/* Post Stats */}
                        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                          <Chip
                            icon={<Favorite sx={{ fontSize: 16 }} />}
                            label={`${post.likeCount} beğeni`}
                            size="small"
                            sx={{
                              bgcolor: 'error.50',
                              color: 'error.main',
                              fontWeight: 600,
                              '& .MuiChip-icon': {
                                color: 'error.main'
                              }
                            }}
                          />
                        </Box>
                      </Box>
                    </Card>
                  ))}
                </Box>
                {statistics.recentActivity.recentPosts.length === 0 && (
                  <Alert severity="info" sx={{ borderRadius: 2 }}>
                    Henüz paylaşım yok
                  </Alert>
                )}
              </CardContent>
            </Card>
        </Box>
      </Container>

      {/* Edit Dialog */}
      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Kafe Bilgilerini Düzenle</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, mt: 2 }}>
            {/* Cafe Name */}
            <TextField
              label="Kafe Adı"
              value={editName}
              onChange={(e) => setEditName(e.target.value)}
              fullWidth
              required
              helperText="Kafenizin görünen adı"
            />

            <Divider />

            {/* Address */}
            <TextField
              label="Adres"
              value={editAddress}
              onChange={(e) => setEditAddress(e.target.value)}
              multiline
              rows={3}
              fullWidth
              helperText="Kafenizin tam adresi"
            />

            <Divider />

            {/* Anonymous Matching Toggle */}
            <FormControlLabel
              control={
                <Switch
                  checked={editAnonymousMatching}
                  onChange={(e) => setEditAnonymousMatching(e.target.checked)}
                  color="primary"
                />
              }
              label={
                <Box>
                  <Typography variant="body1" fontWeight={600}>
                    Anonim Eşleşme
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {editAnonymousMatching
                      ? 'Kafenizde anonim eşleşmeler aktif'
                      : 'Kafenizde anonim eşleşmeler kapalı'}
                  </Typography>
                </Box>
              }
            />

            <Divider />

            {/* Photo Manager */}
            <PhotoManager
              cafeId={cafeOwner?.cafeId || 'temp'}
              photoURLs={editPhotoURLs}
              onPhotosChange={(newPhotoURLs) => {
                setEditPhotoURLs(newPhotoURLs);
                // Update main photo for avatar display
                if (newPhotoURLs.length > 0) {
                  setEditPhoto(newPhotoURLs[0]);
                }
              }}
              maxPhotos={1}
            />

            <Divider />

            {/* Features Selection */}
            <Box>
              <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600 }}>
                Kafe Özellikleri
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
                Kafe'nizde bulunan özellikleri seçin (Maksimum {MAX_FEATURES} özellik)
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {CAFE_FEATURES.map((feature) => {
                  const isSelected = editFeatures.includes(feature.key);
                  const canSelect = isSelected || editFeatures.length < MAX_FEATURES;

                  return (
                    <Chip
                      key={feature.key}
                      label={`${feature.icon} ${feature.label}`}
                      onClick={() => {
                        if (!canSelect && !isSelected) return;
                        const newFeatures = isSelected
                          ? editFeatures.filter(f => f !== feature.key)
                          : [...editFeatures, feature.key];
                        setEditFeatures(newFeatures);
                      }}
                      color={isSelected ? 'primary' : 'default'}
                      variant={isSelected ? 'filled' : 'outlined'}
                      disabled={!canSelect && !isSelected}
                      clickable={canSelect || isSelected}
                      sx={{
                        fontWeight: isSelected ? 600 : 400,
                        opacity: (!canSelect && !isSelected) ? 0.5 : 1
                      }}
                    />
                  );
                })}
              </Box>
              {editFeatures.length > 0 && (
                <Typography variant="caption" color="primary" sx={{ display: 'block', mt: 1 }}>
                  {editFeatures.length}/{MAX_FEATURES} özellik seçildi
                </Typography>
              )}
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>İptal</Button>
          <Button onClick={handleUpdateCafe} variant="contained" disabled={updating}>
            {updating ? <CircularProgress size={24} /> : 'Güncelle'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Password Change Dialog */}
      <Dialog open={passwordDialogOpen} onClose={() => setPasswordDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Lock color="primary" />
            <Typography variant="h6">Şifre Değiştir</Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, mt: 2 }}>
            <TextField
              label="Mevcut Şifre"
              type={showCurrentPassword ? 'text' : 'password'}
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              fullWidth
              required
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                      edge="end"
                    >
                      {showCurrentPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            <TextField
              label="Yeni Şifre"
              type={showNewPassword ? 'text' : 'password'}
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              fullWidth
              required
              helperText="En az 6 karakter olmalıdır"
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowNewPassword(!showNewPassword)}
                      edge="end"
                    >
                      {showNewPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            <TextField
              label="Yeni Şifre (Tekrar)"
              type={showConfirmPassword ? 'text' : 'password'}
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              fullWidth
              required
              helperText="Yeni şifrenizi tekrar girin"
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      edge="end"
                    >
                      {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setPasswordDialogOpen(false);
            setCurrentPassword('');
            setNewPassword('');
            setConfirmPassword('');
          }}>
            İptal
          </Button>
          <Button onClick={handleChangePassword} variant="contained" disabled={changingPassword}>
            {changingPassword ? <CircularProgress size={24} /> : 'Şifreyi Değiştir'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default CafeOwnerDashboardPage;
